import { FC, useMemo } from 'react';
import { observer } from 'mobx-react-lite';

import { MarketDataPackage } from '@/types';
import { useMobx } from '@/hooks/useMobx';

import { CategoryHeader, CategoryHeaderProps } from './CategoryHeader';
import { CategoryPackage } from './CategoryPackage';

export type CategoryProps = {
  teamId: string;
  projectId: string;
  brand: string;
  header: string;
  status: CategoryHeaderProps['status'];
  packages: MarketDataPackage[];
  selectedProperties: string[];
};

export const Category: FC<CategoryProps> = observer((props: CategoryProps) => {
  const { marketData } = useMobx();

  const packagesToRender = useMemo(() => {
    const publishedProperties = marketData.getPublishedPropertyIds(props.projectId);
    return props.packages.reduce(
      (acc, dataPackage) => {
        const selectedPropertyIds =
          marketData.selectedDraftProperties[props.brand]?.[dataPackage.id] || [];
        const visibleProperties = dataPackage.properties.filter((p) => {
          return (
            publishedProperties.has(p.id) ||
            (dataPackage.publicly_visible && p.publicly_visible) ||
            selectedPropertyIds.includes(p.id)
          );
        });

        if (visibleProperties.length > 0) {
          acc.push({ dataPackage, visibleProperties });
        }

        return acc;
      },
      [] as { dataPackage: MarketDataPackage; visibleProperties: MarketDataPackage['properties'] }[]
    );
  }, [
    props.packages,
    props.projectId,
    props.brand,
    marketData.selectedDraftProperties,
    marketData,
  ]);

  const propertyCount = useMemo(() => {
    return packagesToRender.reduce((acc, { dataPackage, visibleProperties }) => {
      if (props.status === 'draft') {
        const selectedPropertyIds =
          marketData.selectedDraftProperties[props.brand]?.[dataPackage.id] || [];
        const visibleAndSelectedCount = visibleProperties.filter((p) =>
          selectedPropertyIds.includes(p.id)
        ).length;

        return acc + visibleAndSelectedCount;
      }

      return acc + visibleProperties.length;
    }, 0);
  }, [packagesToRender, props.status, props.brand, marketData.selectedDraftProperties]);

  return (
    <div className="gap-4 py-4">
      <CategoryHeader status={props.status} propertyCount={propertyCount}>
        {props.header}
      </CategoryHeader>
      <div>
        {packagesToRender.map(({ dataPackage, visibleProperties }, i) => (
          <CategoryPackage
            key={i}
            package={dataPackage}
            selectedProperties={props.selectedProperties}
            teamId={props.teamId}
            projectId={props.projectId}
            brand={props.brand}
            status={props.status}
            visibleProperties={visibleProperties}
          />
        ))}
      </div>
    </div>
  );
});
